// SyncBus — sanntids-buss mellom SKADE-vinduer/faner på samme opphav.
//
// Fundamentet for multi-skjerm: når et panel poppes ut i eget vindu, deler alle
// vinduer UI-tilstand (f.eks. «fokusér dette objektet») via BroadcastChannel.
// Selve situasjonsdataene går fortsatt gjennom hubbens WebSocket (hvert vindu
// har sin egen tilkobling), så bussen bærer kun lett koordinering.
//
// BroadcastChannel-fabrikk kan injiseres for testbarhet; mangler den (gammel
// nettleser / test uten støtte) degraderer bussen stille til én-vindu.

export interface SyncMessage {
  type: string;
  [key: string]: unknown;
}
export type SyncHandler = (m: SyncMessage) => void;

export interface Channelish {
  postMessage(m: unknown): void;
  close(): void;
  onmessage: ((ev: { data: unknown }) => void) | null;
}

export class SyncBus {
  private ch: Channelish | null = null;
  private handlers = new Map<string, Set<SyncHandler>>();

  constructor(name = 'skade', factory?: (n: string) => Channelish | null) {
    try {
      if (factory) this.ch = factory(name);
      else if (typeof BroadcastChannel !== 'undefined') this.ch = new BroadcastChannel(name) as unknown as Channelish;
    } catch {
      this.ch = null;
    }
    if (this.ch) this.ch.onmessage = (ev) => this.dispatch(ev.data as SyncMessage);
  }

  /** Kringkast en melding til alle andre vinduer. */
  post(m: SyncMessage): void {
    try { this.ch?.postMessage(m); } catch { /* buss nede — ignorer */ }
  }

  /** Abonnér på en meldingstype. Returnerer en avmeldings-funksjon. */
  on(type: string, h: SyncHandler): () => void {
    let set = this.handlers.get(type);
    if (!set) { set = new Set(); this.handlers.set(type, set); }
    set.add(h);
    return () => { this.handlers.get(type)?.delete(h); };
  }

  private dispatch(m: SyncMessage): void {
    if (!m || typeof m.type !== 'string') return;
    this.handlers.get(m.type)?.forEach((h) => {
      try { h(m); } catch { /* én handler skal ikke velte de andre */ }
    });
  }

  /** Kun for test: simuler en innkommende melding. */
  receive(m: SyncMessage): void {
    this.dispatch(m);
  }

  close(): void {
    try { this.ch?.close(); } catch { /* ignore */ }
    this.ch = null;
    this.handlers.clear();
  }
}
